/**
 * 模块: BrandNetworkGraph配置
 * 职责: 图表样式和力导向图参数配置
 * 限制: 文件<280行
 */

import * as d3 from 'd3';

// 力导向图配置
export const FORCE_CONFIG = {
  linkDistance: 120,    // 增加节点间距：80 -> 120
  chargeStrength: -400, // 增强斥力让布局更分散：-150 -> -400
  collisionRadius: 35,  // 增加碰撞半径避免重叠：20 -> 35
  alphaDecay: 0.015,    // 降低衰减让动画更稳定：0.02 -> 0.015
  velocityDecay: 0.4,
};

// 连线配置
export const EDGE_CONFIG = {
  minWidth: 1,      // 增加最小线宽：0.5 -> 1
  maxWidth: 5,      // 增加最大线宽：3 -> 5
  normalizeWeight: 10,
  opacity: 0.5,     // 增加透明度让连线更明显：0.3 -> 0.5
};

// 节点配置
export const NODE_CONFIG = {
  baseSize: 15,        // 大幅增加基础大小：3 -> 15
  scaleFactor: 0.8,    // 大幅增加缩放因子：0.25 -> 0.8
  minRadius: 25,       // 增加最小半径：12 -> 25
  charPerPixel: 3.5,   // 增加字符像素：2.5 -> 3.5
  textMargin: 1.15,    // 增加文字边距：1.03 -> 1.15
  strokeWidth: 2,      // 增加描边宽度：0.8 -> 2
  hoverStrokeWidth: 4,
  hoverScale: 1.15,
  textHoverScale: 1.1,
};

// 颜色配置
export const EDGE_COLORS = {
  direct: '#8b5a5a',
  indirect: '#8b7a5a',
  potential: '#5a8b5a',
};

// 计算边的颜色
export function getEdgeColor(type: string): string {
  if (type === 'direct') return EDGE_COLORS.direct;
  if (type === 'indirect') return EDGE_COLORS.indirect;
  return EDGE_COLORS.potential;
}

// 计算边的粗细
export function getEdgeWidth(weight: number): number {
  const normalized = Math.min(weight / EDGE_CONFIG.normalizeWeight, 1);
  return EDGE_CONFIG.minWidth + normalized * (EDGE_CONFIG.maxWidth - EDGE_CONFIG.minWidth);
}

// 颜色比例尺
export function createColorScale(maxInfluence: number) {
  return d3.scaleLinear<string>()
    .domain([0, maxInfluence])
    .range(['#e8e8e8', '#4a5568']);
}

// 计算节点半径
export function getNodeRadius(influence: number, label: string): number {
  const { baseSize, scaleFactor, minRadius, charPerPixel, textMargin } = NODE_CONFIG;
  const influenceRadius = baseSize + influence * scaleFactor;
  
  // 根据品牌名长度计算最小半径
  const textLength = label ? label.length : 0;
  const minRadiusForText = Math.max(minRadius, textLength * charPerPixel * textMargin);
  
  return Math.max(minRadiusForText, influenceRadius);
}

// 计算字体大小
export function getFontSize(radius: number, textLength: number): number {
  if (textLength <= 4) return Math.min(18, radius / 2.2);  // 增加：14 -> 18
  if (textLength <= 8) return Math.min(16, radius / 2.5);  // 增加：12 -> 16
  if (textLength <= 12) return Math.min(14, radius / 3);   // 增加：11 -> 14
  return Math.min(12, radius / 3.5);                        // 增加：10 -> 12
}

// 计算文字颜色（根据背景亮度）
export function getTextColor(bgColor: string): string {
  const color = d3.rgb(bgColor);
  const luminance = 0.299 * color.r + 0.587 * color.g + 0.114 * color.b;
  return luminance > 128 ? '#1a1a1a' : '#ffffff';
}

// 计算文字阴影
export function getTextShadow(bgColor: string): string {
  const color = d3.rgb(bgColor);
  const luminance = 0.299 * color.r + 0.587 * color.g + 0.114 * color.b;
  return luminance > 128 
    ? '0 0 2px rgba(255,255,255,0.9), 0 0 4px rgba(255,255,255,0.6)'
    : '0 0 3px rgba(0,0,0,0.9), 0 0 6px rgba(0,0,0,0.6)';
}

